import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  confirmEvidenceDownloadCompletionInAPI,
  confirmSupplementaryDocumentDirectUploadInAPI,
  createSupplementaryDocumentUploadIntentInAPI,
  getSupplementaryDocumentDownloadUrlInAPI,
  isSupplementaryDocumentAccessPersistenceUnavailableError,
  isSupplementaryDocumentAccessStateChangedError,
  isSupplementaryDocumentIntegrityFailedError,
  isSupplementaryDocumentIntegrityVerificationUnavailableError,
  isSupplementaryDocumentUploadStateChangedError,
  isSupplementaryDocumentUploadVerificationUnavailableError,
  listSupplementaryDocumentsInAPI,
  SupplementaryDocumentEvidence,
  uploadSupplementaryDocumentInAPI,
} from '../services/eCMRService';
import { safeConsoleError } from '../utils/safeConsole';

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',').pop() || '' : result);
    };
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });

const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo'));
    reader.readAsArrayBuffer(file);
  });
};

const calculateSha256Hex = async (file: File): Promise<string> => {
  const buffer = await readFileAsArrayBuffer(file);
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const formatSize = (sizeBytes?: number): string => {
  if (!sizeBytes && sizeBytes !== 0) {
    return 'Tamaño no informado';
  }
  if (sizeBytes < 1024) {
    return `${sizeBytes} bytes`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KiB`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MiB`;
};

const formatSafeMimeType = (mimeType?: string): string => {
  if (!mimeType) {
    return 'MIME no informado';
  }

  return /^[a-z0-9][a-z0-9.+-]*\/[a-z0-9][a-z0-9.+-]*$/i.test(mimeType)
    ? mimeType
    : 'MIME disponible';
};

const uploadErrorMessage = 'No se pudo subir el documento suplementario.';
const uploadStateChangedMessage = 'El documento cambio durante la subida. Recarga los adjuntos antes de volver a intentarlo.';
const uploadVerificationUnavailableMessage = 'No se pudo verificar la version archivada del adjunto. Intentalo de nuevo mas tarde.';
const accessStateChangedMessage = 'El documento cambio durante el acceso. Recarga los adjuntos antes de volver a intentarlo.';
const accessPersistenceUnavailableMessage = 'No se pudo registrar el acceso a los adjuntos. Intentalo de nuevo mas tarde.';
const integrityVerificationUnavailableMessage = 'No se pudo verificar la version archivada del adjunto. Intentalo de nuevo mas tarde.';
const integrityFailedMessage = 'La integridad del adjunto no pudo verificarse. Recarga los adjuntos antes de volver a intentarlo.';
const authErrorMessages = new Set([
  'No hay token de autenticación',
  'Sesión expirada. Por favor, inicia sesión nuevamente.',
]);

const formatUploadErrorMessage = (error: unknown): string => {
  if (isSupplementaryDocumentUploadStateChangedError(error)) {
    return uploadStateChangedMessage;
  }
  if (isSupplementaryDocumentUploadVerificationUnavailableError(error)) {
    return uploadVerificationUnavailableMessage;
  }
  if (error instanceof Error && authErrorMessages.has(error.message)) {
    return error.message;
  }

  return uploadErrorMessage;
};

const formatAccessErrorMessage = (error: unknown, fallback: string): string => {
  if (isSupplementaryDocumentAccessStateChangedError(error)) {
    return accessStateChangedMessage;
  }
  if (isSupplementaryDocumentAccessPersistenceUnavailableError(error)) {
    return accessPersistenceUnavailableMessage;
  }
  if (isSupplementaryDocumentIntegrityVerificationUnavailableError(error)) {
    return integrityVerificationUnavailableMessage;
  }
  if (isSupplementaryDocumentIntegrityFailedError(error)) {
    return integrityFailedMessage;
  }
  return fallback;
};

const SupplementaryDocuments: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const documentId = id || '';
  const [attachments, setAttachments] = useState<SupplementaryDocumentEvidence[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('customs_document');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadAttachments = async () => {
    if (!documentId) {
      setAttachments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setAttachments([]);
    try {
      const result = await listSupplementaryDocumentsInAPI(documentId);
      setAttachments(result);
    } catch (error) {
      setAttachments([]);
      safeConsoleError('Error loading supplementary documents', 'supplementary_documents.load');
      setMessage({
        type: 'error',
        text: formatAccessErrorMessage(error, 'No se pudieron cargar los documentos suplementarios.'),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const handleUpload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!documentId || !file) {
      setMessage({ type: 'error', text: 'Selecciona un archivo suplementario antes de subirlo.' });
      return;
    }

    setSubmitting(true);
    setMessage(null);
    try {
      const mimeType = file.type || 'application/octet-stream';
      const descriptionText = description.trim() || undefined;
      const sha256Digest = await calculateSha256Hex(file);
      let uploadIntent;
      try {
        uploadIntent = await createSupplementaryDocumentUploadIntentInAPI(documentId, {
          fileName: file.name,
          mimeType,
          sizeBytes: file.size,
          sha256Digest,
          documentType,
          description: descriptionText,
        });
      } catch {
        const contentBase64 = await readFileAsBase64(file);
        await uploadSupplementaryDocumentInAPI(documentId, {
          fileName: file.name,
          mimeType,
          contentBase64,
          documentType,
          description: descriptionText,
        });
      }

      if (uploadIntent) {
        const uploadResponse = await fetch(uploadIntent.uploadUrl, {
          method: uploadIntent.method || 'PUT',
          headers: uploadIntent.requiredHeaders || { 'Content-Type': mimeType },
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error('No se pudo transferir el archivo al almacenamiento directo');
        }
        await confirmSupplementaryDocumentDirectUploadInAPI(documentId, uploadIntent.attachmentId, {
          fileName: file.name,
          mimeType,
          sizeBytes: file.size,
          sha256Digest,
          documentType,
          description: descriptionText,
        });
      }
      setFile(null);
      setDescription('');
      setMessage({ type: 'success', text: 'Documento suplementario subido y vinculado al e-CMR.' });
      await loadAttachments();
    } catch (error) {
      safeConsoleError('Error uploading supplementary document', 'supplementary_documents.upload');
      setMessage({ type: 'error', text: formatUploadErrorMessage(error) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (attachment: SupplementaryDocumentEvidence) => {
    try {
      const result = await getSupplementaryDocumentDownloadUrlInAPI(documentId, attachment.attachmentId);
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
      await confirmEvidenceDownloadCompletionInAPI(documentId, {
        evidenceId: attachment.attachmentId,
        evidenceType: 'supplementary_attachment',
      });
    } catch (error) {
      safeConsoleError('Error creating supplementary document download URL', 'supplementary_documents.download_url');
      setMessage({
        type: 'error',
        text: formatAccessErrorMessage(error, 'No se pudo crear la URL de descarga auditada.'),
      });
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4">
        <Link to="/" className="text-blue-600 hover:text-blue-800">← Volver a documentos</Link>
      </div>

      <h1 className="text-2xl font-bold mb-4">Documentos suplementarios</h1>
      <p className="mb-6 text-sm text-gray-700 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
        Este flujo expone un <strong>baseline técnico parcial</strong> para documentos suplementarios eCMR: subida
        backend con controles de tamaño/MIME/contenido, listado auditable y creación de URL de descarga con
        verificación de integridad previa. No sustituye controles legales, malware scanning ni retención WORM unificada.
      </p>

      {message && (
        <div className={`mb-4 p-3 rounded ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleUpload} className="bg-white shadow rounded p-4 mb-6 space-y-4">
        <h2 className="text-lg font-semibold">Subir evidencia suplementaria</h2>
        <div>
          <label htmlFor="supplementary-file" className="block text-sm font-medium text-gray-700 mb-1">
            Archivo suplementario
          </label>
          <input
            id="supplementary-file"
            type="file"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-700"
          />
        </div>
        <div>
          <label htmlFor="document-type" className="block text-sm font-medium text-gray-700 mb-1">
            Tipo documental
          </label>
          <select
            id="document-type"
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value)}
            className="border rounded px-3 py-2 w-full"
          >
            <option value="customs_document">Documento aduanero</option>
            <option value="customs_invoice">Factura aduanera</option>
            <option value="packing_list">Lista de bultos</option>
            <option value="dangerous_goods_declaration">Declaración mercancías peligrosas</option>
            <option value="other">Otro</option>
          </select>
        </div>
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Descripción
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="border rounded px-3 py-2 w-full"
            rows={3}
          />
        </div>
        <button
          type="submit"
          disabled={submitting || !file}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
        >
          {submitting ? 'Subiendo...' : 'Subir documento suplementario'}
        </button>
      </form>

      <section className="bg-white shadow rounded p-4">
        <h2 className="text-lg font-semibold mb-3">Evidencia adjunta registrada</h2>
        {loading ? (
          <p>Cargando documentos suplementarios...</p>
        ) : attachments.length === 0 ? (
          <p className="text-gray-600">No hay documentos suplementarios registrados para este e-CMR.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Archivo</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Versión</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Integridad</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {attachments.map((attachment, index) => {
                  const safeLabel = `Documento suplementario ${index + 1}`;
                  return (
                    <tr key={attachment.attachmentId}>
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900">{safeLabel}</div>
                        <div className="text-xs text-gray-500">{formatSafeMimeType(attachment.mimeType)} · {formatSize(attachment.sizeBytes)}</div>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {attachment.documentType ? 'Tipo documental disponible' : 'Tipo documental no informado'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {attachment.documentVersionReferencePresent || attachment.documentVersionReferenceAvailable || attachment.documentVersionNumber !== undefined ? 'Referencia de versión disponible' : 'Sin referencia de versión'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {attachment.digestAvailable ? 'Digest registrado' : 'Digest no informado'}
                        <span className="block text-xs text-gray-500">Valor de integridad oculto en esta vista</span>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => handleDownload(attachment)}
                          className="text-blue-700 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded"
                        >
                          Descargar {safeLabel}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default SupplementaryDocuments;
